import { Link } from 'react-router-dom';
import { ArrowUpRight } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { SERVICES, SERVICE_PAGE_SLUG, FEATURED_SLUGS, VERTICALS, SERVICE_GROUPS } from '../data/site';
import { useSEO } from '../lib/seo';

const GROUP_SECTOR_SLUG = {
  'Commercial Property Maintenance': 'commercial-property-maintenance',
  'IT Support Services': 'it-support-services',
  'Accounting & Payroll Services': 'accounting-payroll-services',
  'Automation Retrofit Services': 'automation-retrofit',
  'Data Centre M&E Services': 'data-centre-mep',
};

const ServicesDirectory = () => {
  useSEO({
    title: 'All Services — 65 Across 6 Sectors | Vox Meridian',
    description: 'Browse all 65 services across six sectors and 10 markets. Post one RFQ and get matched with verified providers.',
    path: '/services',
  });

  let rowNo = 0;

  return (
    <div data-testid="services-directory-page">
      <PageHero
        eyebrow="The full directory"
        lines={[<>65 services.</>, <span key="l2">Six <span className="text-volt">sectors.</span></span>]}
        sub="Industrial services, automation retrofit, data-centre M&E, commercial property maintenance, IT support and accounting & payroll — matched across 10 markets, from 2 a.m. breakdown repairs to monthly payroll. Rows marked FEATURED add market-specific pages for all ten markets."
      />

      <section className="border-b border-line bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
          <Chapter no="01" label="Service index" title="Pick your sector, then your service." />
          {SERVICE_GROUPS.map((g) => {
            const sectorSlug = GROUP_SECTOR_SLUG[g.name];
            return (
              <div key={g.name} className="mt-14 first:mt-12" data-testid={`group-${g.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <Reveal>
                  <p className="mono-label text-volt">{g.trades.length} services</p>
                  {sectorSlug ? (
                    <Link
                      to={`/services/${sectorSlug}`}
                      data-testid={`group-link-${sectorSlug}`}
                      className="mt-2 inline-block font-display text-2xl font-bold text-parchment transition-colors duration-200 hover:text-volt"
                    >
                      {g.name}
                    </Link>
                  ) : (
                    <h3 className="mt-2 font-display text-2xl font-bold text-parchment">{g.name}</h3>
                  )}
                </Reveal>
                <div className="mt-6 border-t border-line">
                  {g.trades.map((tradeName) => {
                    const s = SERVICES.find((x) => x.name === tradeName);
                    if (!s) return null;
                    rowNo += 1;
                    const slug = SERVICE_PAGE_SLUG[s.name];
                    const to = slug ? `/services/${slug}` : `/buyers?category=${encodeURIComponent(s.name)}`;
                    const sid = s.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                    return (
                      <Reveal key={s.name} delay={0.03} y={14}>
                        <Link
                          to={to}
                          data-testid={`service-row-${sid}`}
                          className="group flex items-center gap-5 border-b border-line px-2 py-5 transition-colors duration-200 hover:bg-surface sm:gap-8 sm:px-4"
                        >
                          <span className="w-8 shrink-0 font-mono text-sm font-semibold text-volt/70">
                            {String(rowNo).padStart(2, '0')}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-display text-base font-bold text-parchment transition-colors duration-200 group-hover:text-volt sm:text-xl">
                              {s.name}
                            </p>
                            <p className="mt-1 truncate text-xs text-steel sm:text-sm">{s.examples}</p>
                          </div>
                          {slug && FEATURED_SLUGS.includes(slug) && (
                            <span className="hidden shrink-0 border border-volt/40 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-volt sm:block">
                              Featured
                            </span>
                          )}
                          <ArrowUpRight size={18} className="shrink-0 text-steel transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-volt" />
                        </Link>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <Chapter no="02" label="Focus area" title="Cross-service programme." />
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {VERTICALS.map((v, i) => (
              <Reveal key={v.slug} delay={i * 0.07}>
                <Link
                  to={`/services/${v.slug}`}
                  data-testid={`vertical-card-${v.slug}`}
                  className="group flex h-full flex-col border border-line bg-surface p-8 transition-colors duration-300 hover:border-volt/60"
                >
                  <p className="mono-label text-volt">Focus area</p>
                  <p className="mt-3 font-display text-xl font-bold text-parchment transition-colors duration-200 group-hover:text-volt">
                    {v.name}
                  </p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{v.tagline}</p>
                  <span className="mt-5 flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-steel transition-colors duration-200 group-hover:text-volt">
                    Explore <ArrowUpRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12">
            <div className="flex flex-col items-start gap-6 border border-line bg-surface p-8 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-lg text-sm leading-relaxed text-steel">
                Don&rsquo;t see your exact service? Post anyway — during the concierge phase the founder
                matches every RFQ by hand, including the odd ones. Especially the odd ones.
              </p>
              <Link
                to="/buyers"
                data-testid="directory-cta"
                className="group flex shrink-0 items-center gap-2 bg-volt px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
              >
                Post an RFQ
                <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ServicesDirectory;
